import BarChartIcon from '@mui/icons-material/BarChart';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SettingsIcon from '@mui/icons-material/Settings';
import SpeedIcon from '@mui/icons-material/Speed';
import { Box, Stack } from '@mui/material';
import Container from '@mui/material/Container';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { SpotterDetailsHeader } from '@/components/spotter/SpotterDetailsHeader';
import { TabPanel } from '@/components/spotter/TabPanel';
import { ConfigurationTabContainer } from '@/components/spotter/configurationTab/ConfigurationTabContainer';
import { HistoricalDataTabContainer } from '@/components/spotter/historicalDataTab/ObservationDataTabContainer';
import { NotificationTabContent } from '@/components/spotter/notificationTab/NotificationTabContent';
import { StatusTabContent } from '@/components/spotter/statusTab/StatusTabContent';
import { useUnsavedChangesStore } from '@/store/unsavedChangesStore';

const DEFAULT_TAB = 'observation-data';
const tabs = ['observation-data', 'status', 'notifications', 'configuration'];

export const SpotterDetails = () => {
    const { hash } = useLocation();
    const [currentActiveTab, setCurrentActiveTab] = useState(DEFAULT_TAB);
    const requestNavigation = useUnsavedChangesStore((s) => s.requestNavigation);

    const handleChange = (_event: React.SyntheticEvent, indexOfSelectedTab: number) => {
        const newActiveTabName = tabs[indexOfSelectedTab];
        requestNavigation(() => {
            setCurrentActiveTab(newActiveTabName);
            window.location.hash = newActiveTabName;
        });
    };

    useEffect(() => {
        const urlSection = hash.replace('#', '') || DEFAULT_TAB;
        const isTabAlreadySelected = currentActiveTab === urlSection;
        const isValidTabName = tabs.includes(urlSection);
        if (!isValidTabName || isTabAlreadySelected) {
            return;
        }

        requestNavigation(
            () => {
                setCurrentActiveTab(urlSection);
                window.location.hash = urlSection;
            },
            () => {
                window.location.hash = currentActiveTab;
            }
        );
    }, [hash, currentActiveTab, requestNavigation]);

    return (
        <>
            <Container maxWidth={false}>
                <Box sx={{ width: '100%' }}>
                    <Stack
                        direction="row"
                        sx={{
                            flexWrap: 'wrap',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: 1,
                            borderColor: 'divider',
                        }}
                    >
                        <SpotterDetailsHeader />
                        <Tabs
                            value={tabs.indexOf(currentActiveTab)}
                            onChange={handleChange}
                            aria-label="spotter details tabs"
                            variant="scrollable"
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                            sx={{
                                alignSelf: 'end',
                                '& .MuiTabs-scroller': { justifyContent: 'center', display: 'flex' },
                                '& .MuiTabs-indicator': { backgroundColor: 'info.main' },
                                '& .MuiTab-root': {
                                    minHeight: 5,
                                },
                            }}
                        >
                            <Tab icon={<BarChartIcon />} iconPosition="start" label="Observation Data" />
                            <Tab icon={<SpeedIcon />} iconPosition="start" label="Status" />
                            <Tab icon={<NotificationsIcon />} iconPosition="start" label="Notifications" />
                            <Tab icon={<SettingsIcon />} iconPosition="start" label="Settings" />
                        </Tabs>
                    </Stack>

                    <TabPanel currentActiveTab={currentActiveTab} sx={{ my: 3 }} tabName="observation-data">
                        <HistoricalDataTabContainer />
                    </TabPanel>
                    <TabPanel currentActiveTab={currentActiveTab} tabName="status">
                        <Container maxWidth="md" sx={{ justifyContent: 'center' }}>
                            <StatusTabContent />
                        </Container>
                    </TabPanel>
                    <TabPanel currentActiveTab={currentActiveTab} tabName="notifications">
                        <Container maxWidth="md" sx={{ justifyContent: 'center' }}>
                            <NotificationTabContent />
                        </Container>
                    </TabPanel>
                    <TabPanel currentActiveTab={currentActiveTab} tabName="configuration">
                        <Container maxWidth="md" sx={{ justifyContent: 'center' }}>
                            <ConfigurationTabContainer />
                        </Container>
                    </TabPanel>
                </Box>
            </Container>
        </>
    );
};
